import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { FINANCE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author @muhammedburakak
 */

const BankTransferModal = NiceModal.create(({ ID, clientName, amount, approveMode, clientID, currency }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const defaultColSpan = {
		md: 6
	};

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					amount: yup.number().when('approveMode', {
						is: true,
						then: (schema) => schema.required()
					}),
					description: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			clientName,
			amount,
			description: approveMode ? 'Bank transfer approved' : 'Bank transfer rejected',
			currency
		}
	});

	const { handleSubmit, setValue, watch } = formMethods;

	const onSubmit = async (data) => {
		setIsLoading(true);

		const URL = approveMode ? FINANCE.APPROVE_BANK_TRANSFER : FINANCE.REJECT_BANK_TRANSFER;

		try {
			const response = await axios.post(URL, {
				ID,
				clientID,
				amount: data.amount ?? undefined,
				description: data.description
			});
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.BANK_TRANSFER));
				showSuccess();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
			modal.remove();
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="modal-bankTransferModal">
			<ModalTitle>
				<Typography variant="p1">{approveMode ? t('approveBankTransfer') : t('rejectBankTransfer')}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="clientName">
						<StringInput
							name="clientName"
							value={watch('clientName')}
							disabled
							setValue={(value) => {
								setValue('clientName', value);
							}}
						/>
					</FormItem>

					{approveMode && (
						<FormItem label="bankTransferModal.amount" colSpan={defaultColSpan} required>
							<IntegerInput name="amount" allowFloat unit={currency} />
						</FormItem>
					)}

					<FormItem label="description" colSpan={defaultColSpan} required>
						<StringInput
							name="description"
							value={watch('description')}
							setValue={(value) => {
								setValue('description', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					label={approveMode ? t('approve') : t('reject')}
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default BankTransferModal;
